import { ALL_EXAMPLE_NOVEL_IDS, EXAMPLE_NOVEL_OWNER_ID } from '@/config/example-novel-config';
import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import { updateDocumentNonBlocking } from '@/firebase/non-blocking-updates';
import { EMPTY_NOVEL, EMPTY_NOVEL_SUMMARIES, Novel } from '@/lib/types';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface SelectedNovel {
  ownerId: string;
  novelId: string;
}

interface NovelContextType {
  novel: Novel;
  selectedNovel: SelectedNovel | null;
  selectedNovelId: string | null;
  setSelectedNovel: (selected: SelectedNovel | string | null) => void;
  setSelectedNovelId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
  /** True when the currently selected novel is a read-only ePlotera example novel. */
  isExampleNovel: boolean;
}

const NovelContext = createContext<NovelContextType | undefined>(undefined);

export function NovelProvider({ children }: { children: ReactNode }) {
  const { backend, user } = useFirebase();
  const { userData, isLoading: userProfileLoading, docRef: userRef } = useUserProfile();

  const [selectedNovel, setSelectedNovelState] = useState<SelectedNovel | null>(null);
  const [novel, setNovel] = useState<Novel>(EMPTY_NOVEL);
  const [novelLoading, setNovelLoading] = useState(false);
  const [novelError, setNovelError] = useState<Error | null>(null);

  // Sync internal state with user profile preference on load
  useEffect(() => {
    const currentId = userData?.currentNovelId;
    if (currentId) {
      if (currentId.includes(';')) {
        const [ownerId, novelId] = currentId.split(';');
        setSelectedNovelState(prev => prev?.ownerId === ownerId && prev?.novelId === novelId ? prev : { ownerId, novelId });
      } else if (user) {
        setSelectedNovelState(prev => prev?.ownerId === user.uid && prev?.novelId === currentId ? prev : { ownerId: user.uid, novelId: currentId });
      }
    } else if (!userProfileLoading) {
      setSelectedNovelState(prev => prev === null ? prev : null);
    }
  }, [userData, userProfileLoading, user]);
  const setSelectedNovel = useCallback((selected: SelectedNovel | string | null) => {
    if (!selected) {
      setSelectedNovelState(null);
      if (userRef) {
        updateDocumentNonBlocking(userRef, { currentNovelId: null });
      }
      return;
    }

    let parsed: SelectedNovel;
    let rawStr: string;

    if (typeof selected === 'string') {
      rawStr = selected;
      if (selected.includes(';')) {
        const [ownerId, novelId] = selected.split(';');
        parsed = { ownerId, novelId };
      } else {
        parsed = { ownerId: user?.uid || '', novelId: selected };
      }
    } else {
      parsed = selected;
      rawStr = parsed.ownerId === user?.uid ? parsed.novelId : `${parsed.ownerId};${parsed.novelId}`;
    }

    setSelectedNovelState(parsed);

    if (userRef) {
      updateDocumentNonBlocking(userRef, { currentNovelId: rawStr });
    }
  }, [user?.uid, userRef]);

  const setSelectedNovelId = useCallback((id: string | null) => {
    setSelectedNovel(id);
  }, [setSelectedNovel]);

  const selectedNovelId = useMemo(() => {
    if (!selectedNovel) return null;
    return selectedNovel.ownerId === user?.uid
      ? selectedNovel.novelId
      : `${selectedNovel.ownerId};${selectedNovel.novelId}`;
  }, [selectedNovel, user]);

  // Subscribe once per selected novel/backend identity. Snapshot callbacks deliver updates.
  useEffect(() => {
    if (!selectedNovel || !backend) {
      setNovel(EMPTY_NOVEL);
      setNovelLoading(false);
      setNovelError(null);
      return;
    }

    setNovelLoading(true);
    setNovelError(null);

    const unsubscribe = backend.novels.subscribeNovel(
      selectedNovel.ownerId,
      selectedNovel.novelId,
      (fullNovel) => {
        if (fullNovel) {
          setNovel({
            ...fullNovel,
            summaries: {
              ...EMPTY_NOVEL_SUMMARIES,
              ...fullNovel.summaries,
            },
          });
        } else {
          setNovel(EMPTY_NOVEL);
        }
        setNovelLoading(false);
      },
      (err) => {
        setNovelError(err);
        setNovelLoading(false);
      },
    );

    return unsubscribe;
  }, [selectedNovel, backend]);
  const hasNovelInProfile = !!userData?.currentNovelId;
  const isProfileLoading = !selectedNovelId && userProfileLoading;
  const isLoadingTotal = isProfileLoading || novelLoading || (hasNovelInProfile && !selectedNovel) || (!!selectedNovel && !novel.id && !novelError);

  const isExampleNovel = useMemo(() => {
    if (!selectedNovel) return false;
    return (
      selectedNovel.ownerId === EXAMPLE_NOVEL_OWNER_ID &&
      ALL_EXAMPLE_NOVEL_IDS.includes(selectedNovel.novelId)
    ) || novel.isExample === true;
  }, [selectedNovel, novel.isExample]);

  return (
    <NovelContext.Provider value={{
      novel,
      selectedNovel,
      selectedNovelId,
      setSelectedNovel,
      setSelectedNovelId,
      isLoading: isLoadingTotal,
      error: novelError,
      isExampleNovel,
    }}>
      {children}
    </NovelContext.Provider>
  );
}

export function useNovel() {
  const context = useContext(NovelContext);
  if (context === undefined) {
    throw new Error('useNovel must be used within a NovelProvider');
  }
  return context;
}
